import React, { useContext, useCallback, useState } from 'react'
import { Typography, Tag, App } from 'antd'
import { PiSparkle, PiBatteryChargingBold } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import { useHistory, useLocation } from 'react-router-dom'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { IEnergyScoreMetaData, PTOSuggestionsContextInfo, EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { QuizAnswers, RechargeType } from '@vacationtracker/shared/types/recharge-type'
import { getRechargeTypeInfo } from '../../../../util/recharge-type-info'
import { PTOSuggestionsResponse, PTOSuggestion } from '../../types'
import { setSuggestionContext } from '../../services/suggestionFollowThrough'
import { ThemeContext } from '../../../../context/themeContext'
import SuggestedActionCard from './suggested-action-card'
import { useWeekPreferences } from './hooks/useWeekPreferences'
import { getStatusLabelType } from './utils/statusLabel'
import { NoSuggestionsState } from './components/no-suggestions-state'
import { NoPtoBalanceState } from './components/no-pto-balance-state'
import { PreVacationCheckin } from './components/pre-vacation-checkin'
import { PostVacationCheckin } from './components/post-vacation-checkin'
import { normalizeISODateString } from './utils/dateUtils'
import { RechargeTypeIntroModal, RechargeTypeQuiz, RechargeTypeResult } from '../../../../components/recharge-type'
import { useSaveUserRechargeType } from '../../../../services/api/recharge-type-hooks'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { setRechargeType } from '../../../../store/recharge-type-slice'
import { selectUserIdSlice } from '../../../../store/user-id-slice'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { rechargeTypeTracking } from '../../../../components/recharge-type/services/rechargeTypeTracking'

const { Title, Text } = Typography

interface SuggestedActionsProps {
  ptoSuggestions: PTOSuggestionsResponse
  daysSinceLastBreak?: number
  currentEnergyScore?: number
  userName?: string
  meta?: IEnergyScoreMetaData
  rechargeType?: RechargeType | null
  onSuggestedLeaveClick?: (suggestion: PTOSuggestion, suggestionIndex: number, isBestSuggestion: boolean,
    clickSource?: 'suggestion_card' | 'quick_action' | 'detailed_view') => void
  onTooltipView?: (tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode',
    tooltipContent: string, elementHovered: string, section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown', hoverDurationMs: number) => void
  onActionTaken?: (actionType: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click', actionName: string,
    elementType: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion',
    section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown', ledToNavigation?: boolean, destinationUrl?: string) => void
  onFeedbackLinkClick?: (location: 'header' | 'footer' | 'energy_gauge' | 'suggestions' | 'score_breakdown' | 'error_state') => void
  // Checkin state props
  balanceResetDate?: string
  vacationDates?: string
  daysUntilLeave?: number
  nextRecommendationDate?: string
  previousCheckinMode?: EnergyMode | null
  onCheckinEnergySelect?: (mode: EnergyMode) => void
  onRechargeTypeComplete?: () => void
}

const SuggestedActions: React.FC<SuggestedActionsProps> = ({
  ptoSuggestions,
  currentEnergyScore,
  rechargeType,
  onSuggestedLeaveClick,
  balanceResetDate,
  vacationDates,
  daysUntilLeave,
  nextRecommendationDate,
  previousCheckinMode,
  onCheckinEnergySelect,
  onRechargeTypeComplete,
}) => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const location = useLocation()
  const { theme } = useContext(ThemeContext)
  const isDarkTheme = theme === DARK_THEME
  const dispatch = useAppDispatch()
  const { userId } = useAppSelector(selectUserIdSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  // Recharge type quiz state
  const [showIntroModal, setShowIntroModal] = useState(false)
  const [showRechargeQuiz, setShowRechargeQuiz] = useState(false)
  const [showRechargeResult, setShowRechargeResult] = useState(false)
  const [resultRechargeType, setResultRechargeType] = useState<RechargeType | null>(null)

  const { mutateAsync: saveRechargeType } = useSaveUserRechargeType()

  // Get week preferences using custom hook
  const { weekStartsOn, workWeekDays } = useWeekPreferences({ ptoSuggestions })

  // Determine special state based on contextInfo
  const contextType = ptoSuggestions.contextInfo?.type

  const handleRequestTimeOff = useCallback((suggestion: PTOSuggestion, suggestionIndex: number, isBestOption: boolean) => {
    if (!suggestion.leaveTypeId) {
      notification.error({
        message: formatMessage({ id: 'error.leaveSubmitError' }),
        description: formatMessage({ id: 'energyLevel.errorDetermineLeaveType' }),
        duration: 5,
      })
      return
    }

    // Track the suggestion click
    if (onSuggestedLeaveClick) {
      onSuggestedLeaveClick(suggestion, suggestionIndex, isBestOption, 'suggestion_card')
    }

    // Store suggestion context in sessionStorage for follow-through tracking
    const score = currentEnergyScore || suggestion.energyPrediction?.currentScore || 0
    setSuggestionContext({
      suggestionIndex,
      isBestSuggestion: isBestOption,
      startDate: suggestion.startDate,
      endDate: suggestion.endDate,
      durationDays: suggestion.durationDays,
      ptoDaysUsed: suggestion.ptoDaysUsed,
      leaveTypeId: suggestion.leaveTypeId || '',
      predictedEnergyImprovement: suggestion.energyPrediction.improvement,
      improvementPercentage: suggestion.energyPrediction.improvementPercentage,
      confidenceLevel: suggestion.energyPrediction.confidence,
      currentEnergyScore: score,
      energyMode: getEnergyMode(score),
      overlappingUsers: suggestion.overlappingUsers,
      extendedByHolidays: suggestion.extendedByHolidays,
      totalSuggestionsAvailable: ptoSuggestions.suggestions.length,
      clickTimestamp: new Date().toISOString(),
    })

    // Navigate to request leave page with prefilled data
    // Extract date part (YYYY-MM-DD) from ISO strings to ensure timezone-independent dates
    const params = new URLSearchParams({
      startDate: normalizeISODateString(suggestion.startDate),
      endDate: normalizeISODateString(suggestion.endDate),
      leaveTypeId: suggestion.leaveTypeId,
      reason: formatMessage({ id: 'energyLevel.aiSuggestedReason' }),
      returnTo: location.pathname + location.search,
    })

    history.push(`/app/leaves/request-leave?${params.toString()}`)
  }, [onSuggestedLeaveClick, history, location, formatMessage, notification, currentEnergyScore, ptoSuggestions])

  const handleRechargeQuizComplete = useCallback(async (type: RechargeType, answers: QuizAnswers) => {
    try {
      await saveRechargeType({ rechargeType: type, quizAnswers: answers })
      dispatch(setRechargeType({ rechargeType: type, completedAt: new Date().toISOString() }))
      setResultRechargeType(type)
      setShowRechargeQuiz(false)
      setShowRechargeResult(true)
      // Notify parent to refetch data
      onRechargeTypeComplete?.()
    } catch (error) {
      notification.error({
        message: formatMessage({ id: 'error.generic' }),
        duration: 5,
      })
    }
  }, [saveRechargeType, dispatch, notification, formatMessage, onRechargeTypeComplete])

  const getEnergyIcon = (level: string) => {
    switch (level) {
      default: return <PiBatteryChargingBold />
    }
  }

  // Get the personalized message for the user's recharge type
  const getRechargeTypeMessage = (type: RechargeType): { emoji: string; messageId: string } => {
    const info = getRechargeTypeInfo(type, formatMessage)
    const messageIds: Record<RechargeType, string> = {
      [RechargeType.DEEP_RESTORER]: 'energyLevel.rechargeTypeMessage.deepRestorer',
      [RechargeType.SOCIAL_SPRINTER]: 'energyLevel.rechargeTypeMessage.socialSprinter',
      [RechargeType.STRUCTURED_SOCIALIZER]: 'energyLevel.rechargeTypeMessage.structuredSocializer',
      [RechargeType.SPONTANEOUS_SOLOIST]: 'energyLevel.rechargeTypeMessage.spontaneousSoloist',
    }
    return { emoji: info.emoji, messageId: messageIds[type] }
  }

  // Use suggestions in the order received from API (already optimally sorted with isBestSuggestion flag)
  // Put the best suggestion first, then maintain API order for the rest
  const bestSuggestion = ptoSuggestions.suggestions.find(s => s.isBestSuggestion)
  const otherSuggestions = ptoSuggestions.suggestions.filter(s => !s.isBestSuggestion)

  const sortedSuggestions = bestSuggestion
    ? [bestSuggestion, ...otherSuggestions]
    : ptoSuggestions.suggestions
  const userEnergyScore = currentEnergyScore || bestSuggestion?.energyPrediction?.currentScore || 0
  const energyLevel = getEnergyMode(userEnergyScore)
  const energyIcon = getEnergyIcon(energyLevel)

  // Render special states based on contextInfo
  const renderSpecialState = () => {
    // Check for insufficient balance (no PTO)
    if (contextType === PTOSuggestionsContextInfo.INSUFFICIENT_BALANCE && balanceResetDate) {
      return <NoPtoBalanceState balanceResetDate={balanceResetDate} />
    }

    // Check for upcoming leave (pre-vacation check-in)
    if (contextType === PTOSuggestionsContextInfo.UPCOMING_LEAVE && vacationDates && daysUntilLeave !== undefined) {
      return (
        <PreVacationCheckin
          previousFeedbackMode={previousCheckinMode}
          vacationDates={vacationDates}
          daysUntilLeave={daysUntilLeave}
          onEnergySelect={onCheckinEnergySelect}
        />
      )
    }

    // Check for recent return (post-vacation check-in)
    if (contextType === PTOSuggestionsContextInfo.RECENT_RETURN && vacationDates && nextRecommendationDate) {
      return (
        <PostVacationCheckin
          previousFeedbackMode={previousCheckinMode}
          vacationDates={vacationDates}
          nextRecommendationDate={nextRecommendationDate}
          onEnergySelect={onCheckinEnergySelect}
        />
      )
    }

    return null
  }

  const specialState = renderSpecialState()

  // If we have a special state, render it instead of the normal content
  if (specialState) {
    return (
      <div className={`suggested-actions energy-level-${energyLevel}`} data-theme={isDarkTheme ? 'dark' : 'light'}>
        <div className="suggested-actions-header">
          <div className="header-top-section">
            <Title level={4} className="suggestions-title">
              <span className="title-icon-wrapper">
                <span className="icon-container">{energyIcon}</span>
                <IntlMessages id="energyLevel.yourNextBestMoves" />
              </span>
            </Title>
            <Tag color="purple">
              <PiSparkle />
              <IntlMessages id="energyLevel.aiSuggestionsTag" />
            </Tag>
          </div>
        </div>

        <div className="header-separator" />

        {specialState}
      </div>
    )
  }

  return (
    <div className={`suggested-actions energy-level-${energyLevel}`} data-theme={isDarkTheme ? 'dark' : 'light'}>
      <div className="suggested-actions-header">
        <div className="header-top-section">
          <Title level={4} className="suggestions-title">
            <span className="title-icon-wrapper">
              <span className="icon-container">{energyIcon}</span>
              <IntlMessages id="energyLevel.yourNextBestMoves" />
            </span>
          </Title>
          <Tag color="purple">
            <PiSparkle />
            <IntlMessages id="energyLevel.aiSuggestionsTag" />
          </Tag>
        </div>
      </div>

      <div className="header-separator" />

      {rechargeType ? (
        <div className="recharge-type-message">
          <Text>
            <span className="recharge-type-emoji">{getRechargeTypeMessage(rechargeType).emoji}</span>
            {' '}<IntlMessages id={getRechargeTypeMessage(rechargeType).messageId} />
            {' '}<a onClick={() => {
              // Track "Not you?" click (retake quiz)
              if (userId && authCompany?.id) {
                rechargeTypeTracking.trackQuizRetakeClicked({
                  userId,
                  companyId: authCompany.id,
                  previousRechargeType: rechargeType,
                })
              }
              setShowIntroModal(true)
            }} className="not-you-link">
              <IntlMessages id="energyLevel.rechargeTypeNotYou" />
            </a>
          </Text>
        </div>
      ) : (
        <div className="recharge-type-cta">
          <Text>
            <a onClick={() => {
              // Track "Discover your Recharge Type" click
              if (userId && authCompany?.id) {
                rechargeTypeTracking.trackDiscoverTypeClicked({
                  userId,
                  companyId: authCompany.id,
                  source: 'energy-level-page',
                })
              }
              setShowIntroModal(true)
            }}>
              <IntlMessages id="energyLevel.discoverRechargeType" />
            </a>
            {' '}<IntlMessages id="energyLevel.discoverRechargeTypeSuffix" />
          </Text>
        </div>
      )}

      {sortedSuggestions.length === 0 ? (
        <NoSuggestionsState ptoSuggestions={ptoSuggestions} />
      ) : (
        sortedSuggestions.map((suggestion, index) => {
          const isBestOption = suggestion.isBestSuggestion

          // Determine status label type based on suggestion properties
          const statusLabelType = getStatusLabelType(suggestion)

          return (
            <SuggestedActionCard
              key={`${suggestion.startDate}_${suggestion.endDate}`}
              suggestion={suggestion}
              index={index}
              isBestOption={isBestOption}
              statusLabelType={statusLabelType}
              weekStartsOn={weekStartsOn}
              workWeekDays={workWeekDays}
              nonWorkingDays={ptoSuggestions.nonWorkingDays}
              onRequestClick={(suggestion, index, isBestOption) => {
                handleRequestTimeOff(suggestion, index, isBestOption)
              }}
            />
          )
        })
      )}

      <RechargeTypeIntroModal
        visible={showIntroModal}
        currentRechargeType={rechargeType}
        onStartQuiz={() => {
          setShowIntroModal(false)
          setShowRechargeQuiz(true)
        }}
        onCancel={() => {
          // Track intro modal dismissed
          if (userId && authCompany?.id) {
            rechargeTypeTracking.trackIntroModalDismissed({
              userId,
              companyId: authCompany.id,
              source: 'energy-level-page',
              isRetakeMode: !!rechargeType,
            })
          }
          setShowIntroModal(false)
        }}
      />

      <RechargeTypeQuiz
        visible={showRechargeQuiz}
        onComplete={(type, answers) => {
          void handleRechargeQuizComplete(type, answers)
        }}
        onCancel={() => setShowRechargeQuiz(false)}
        source="energy-level-page"
      />

      <RechargeTypeResult
        visible={showRechargeResult}
        rechargeType={resultRechargeType}
        onClose={() => setShowRechargeResult(false)}
      />
    </div>
  )
}

export default SuggestedActions
